import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, ExternalLink, Calendar, Phone, Wrench, Truck, Printer, MapPin } from 'lucide-react';
import axios from '../lib/axios';
import Layout from '../components/Layout';
import { useLocationContext } from '../context/LocationContext';

function getCategoryIcon(category) {
  if (category?.includes('Workshop')) return Wrench;
  return Truck;
}

function formatWeek(start, end) {
  if (!start || !end) return '—';
  const s = new Date(start + 'T00:00:00');
  const e = new Date(end + 'T00:00:00');
  e.setDate(e.getDate() - 1); // inclusive end (Wednesday)
  return `${s.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} – ${e.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}`;
}

function formatDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true });
}

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800',
  sent: 'bg-blue-100 text-blue-800',
  no_response: 'bg-orange-100 text-orange-800',
  failed: 'bg-red-100 text-red-700',
};

export default function TBCSearch() {
  const [inputValue, setInputValue] = useState('');
  const [query, setQuery] = useState('');
  const { selectedLocationId } = useLocationContext();

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['tbc-search', query],
    queryFn: async () => {
      if (!query || query.length < 2) return { results: [] };
      const { data } = await axios.get(`/api/admin/tbc-search?q=${encodeURIComponent(query)}`);
      return data;
    },
    enabled: query.length >= 2,
  });

  const results = data?.results || [];

  const handleSearch = (e) => {
    e.preventDefault();
    setQuery(inputValue.trim());
  };

  const openJobInZuper = (jobUid) => {
    window.open(`https://web.zuperpro.com/jobs/${jobUid}/details`, '_blank');
  };

  const handlePrint = () => {
    window.print();
  };

  // Group results by customer for a cleaner print view
  const grouped = results.reduce((acc, r) => {
    const key = r.customer_uid || r.customer_name;
    if (!acc[key]) {
      acc[key] = { name: r.customer_name, phone: r.customer_phone, jobs: [] };
    }
    acc[key].jobs.push(r);
    return acc;
  }, {});

  return (
    <Layout>
      <div className="flex-1 overflow-y-auto p-3 md:p-6 bg-[#f5f8f6]">
        <div className="max-w-full space-y-4">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-xl font-bold text-brand-dark">Search Jobs</h1>
              <p className="text-sm text-brand-neutral mt-0.5">
                Search all upcoming TBC jobs by customer name or asset
              </p>
            </div>
            {results.length > 0 && (
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-brand-dark bg-white border border-brand-light rounded-xl hover:bg-brand-light/50 transition-colors print:hidden"
              >
                <Printer className="w-4 h-4" />
                Print
              </button>
            )}
          </div>

          {/* Search bar */}
          <form onSubmit={handleSearch} className="flex gap-2 print:hidden">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-neutral" />
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Search by customer name, asset name or serial number..."
                className="w-full pl-10 pr-4 py-2.5 border border-brand-light rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary bg-white"
                autoFocus
              />
            </div>
            <button
              type="submit"
              className="px-5 py-2.5 bg-brand-primary text-white text-sm font-medium rounded-xl hover:bg-brand-primary/90 transition-colors"
            >
              Search
            </button>
          </form>

          {/* Loading */}
          {(isLoading || isFetching) && query.length >= 2 && (
            <div className="text-center py-8 text-brand-neutral text-sm">Searching...</div>
          )}

          {/* No results */}
          {!isLoading && !isFetching && query.length >= 2 && results.length === 0 && (
            <div className="text-center py-12 bg-white rounded-2xl border border-brand-light/60">
              <Search className="w-8 h-8 text-brand-neutral/40 mx-auto mb-2" />
              <p className="text-brand-neutral text-sm">No TBC jobs found for "{query}"</p>
            </div>
          )}

          {/* Results */}
          {results.length > 0 && (
            <>
              <p className="text-xs text-brand-neutral print:hidden">
                {results.length} TBC job{results.length !== 1 ? 's' : ''} found for "{query}"
              </p>

              {/* Print header */}
              <div className="hidden print:block mb-4">
                <h2 className="text-lg font-bold">TBC Schedule — {query}</h2>
                <p className="text-sm text-gray-500">Printed {new Date().toLocaleDateString('en-AE')}</p>
              </div>

              {/* Grouped by customer for print / flat for screen */}
              <div className="space-y-3 print:space-y-4">
                {Object.values(grouped).map((group) => (
                  <div key={group.name} className="bg-white rounded-2xl border border-brand-light/60 overflow-hidden print:border print:border-gray-300 print:rounded-none">
                    {/* Customer header */}
                    <div className="px-4 py-3 bg-brand-light/30 border-b border-brand-light/60 flex items-center justify-between print:bg-gray-100">
                      <div>
                        <p className="font-semibold text-brand-dark text-sm">{group.name}</p>
                        {group.phone && (
                          <p className="text-xs text-brand-neutral flex items-center gap-1 mt-0.5">
                            <Phone className="w-3 h-3" /> {group.phone}
                          </p>
                        )}
                      </div>
                      <span className="text-xs font-medium text-brand-neutral bg-white border border-brand-light rounded-full px-2.5 py-0.5 print:hidden">
                        {group.jobs.length} job{group.jobs.length !== 1 ? 's' : ''}
                      </span>
                    </div>

                    {/* Jobs */}
                    <div className="divide-y divide-brand-light/40">
                      {group.jobs.map((job) => {
                        const CategoryIcon = getCategoryIcon(job.job_category);
                        return (
                          <div key={job.id} className="px-4 py-3 flex items-start gap-3">
                            <div className="flex-1 min-w-0 space-y-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-medium text-white bg-brand-dark">
                                  <CategoryIcon className="w-3 h-3" />
                                  {job.job_category || 'N/A'}
                                </span>
                                <span className="text-xs font-mono text-brand-neutral">
                                  WO #{job.work_order_number || '—'}
                                </span>
                              </div>
                              {job.asset_name && (
                                <p className="text-sm text-brand-dark">
                                  {job.asset_code ? `${job.asset_code} – ` : ''}{job.asset_name}
                                  {job.asset_serial_number && (
                                    <span className="text-xs text-brand-neutral font-mono ml-1">({job.asset_serial_number})</span>
                                  )}
                                </p>
                              )}
                              <div className="flex items-center gap-3 text-xs text-brand-neutral flex-wrap">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  Week of {job.week_start} – {job.week_end}
                                </span>
                                {job.workshop_location && (
                                  <span className="flex items-center gap-1">
                                    <MapPin className="w-3 h-3" /> {job.workshop_location}
                                  </span>
                                )}
                              </div>
                            </div>
                            <button
                              onClick={() => openJobInZuper(job.job_uid)}
                              className="flex items-center gap-1 text-[11px] text-brand-primary hover:text-brand-secondary transition-colors print:hidden flex-shrink-0"
                              title="Open in Zuper"
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                              Zuper
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Empty state before search */}
          {query.length < 2 && !isLoading && (
            <div className="text-center py-16 text-brand-neutral">
              <Search className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Enter a customer name, asset name, or serial number to search</p>
            </div>
          )}

        </div>
      </div>
    </Layout>
  );
}
